import React from 'react';
import { useSelector } from 'react-redux';
import { useGetMySubscriptionQuery } from '../../services/subscriptionApi';
import EmployeeLoanDashboard from './EmployeeLoanDashboard';
import AdminLoanManagement from './AdminLoanManagement';

const Loan = () => {
  const { user } = useSelector((state) => state.auth);
  const { data: subscriptionData, isLoading, error } = useGetMySubscriptionQuery();

  // Check if loan module is accessible
  const isLoanAccessible = () => {
    // Super admin always has access
    if (user?.role === 'super_admin') return true;
    
    // Normalize subscription shape
    const sub = subscriptionData ? (subscriptionData.data ? subscriptionData.data : subscriptionData) : null;
    if (!sub) return false;

    // Check if trial is active
    if (sub.status === 'trial') {
      const now = new Date();
      const trialEndDate = sub.trialEndDate ? new Date(sub.trialEndDate) : null;
      if (trialEndDate && now > trialEndDate) return false; // Trial expired
    }

    // Check if plan includes loan module (DB modules use 'loans')
    const planFeatures = sub.planId?.features;
    return planFeatures?.modules?.includes('loans') || false;
  };

  // Show loading state
  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <div className="text-red-800 mb-4">
            <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-red-800 mb-2">Error Loading Subscription</h3>
          <p className="text-red-700 mb-4">
            Unable to load subscription information. Please try refreshing the page.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md font-medium"
          >
            Refresh Page
          </button>
        </div>
      </div>
    );
  }

  // Check access
  if (!isLoanAccessible()) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
          <div className="text-yellow-800 mb-4">
            <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-yellow-800 mb-2">Access Restricted</h3>
          <p className="text-yellow-700 mb-4">
            {subscriptionData?.status === 'trial' && new Date() > new Date(subscriptionData.trialEndDate)
              ? 'Your trial period has expired. Please upgrade your subscription to access the Loan Management module.'
              : 'Your current plan does not include access to the Loan Management module. Please upgrade your plan to access this feature.'
            }
          </p>
          <button
            onClick={() => window.location.href = '/dashboard'}
            className="bg-yellow-600 hover:bg-yellow-700 text-white px-4 py-2 rounded-md font-medium"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  // Show admin interface for company admins and super admins
  if (user?.role === 'company_admin' || user?.role === 'super_admin') {
    return <AdminLoanManagement />;
  }

  // Show employee interface for regular employees
  return <EmployeeLoanDashboard />;
};

export default Loan;